import { useBreadcrumbTitle } from "@msuite/ui/app-shell";
import { ContentBox } from "@msuite/ui/content-box";
import { useFileUpload } from "@msuite/ui/file-upload";
import { resolver } from "@msuite/ui/libs/form";
import { LoadingScreen } from "@msuite/ui/loading-screen";
import { useTranslation } from "@msuite/ui/localization";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { toast } from "@msuite/ui/toast";
import { responseHandler } from "@msuite/ui/utils";
import { type ProtocolFormSchema, protocolFormSchema } from "@msuite/utils/schemas";
import type { Protocol } from "@msuite/utils/types";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useNavigate, useParams } from "react-router";
import { useUpload } from "@/api/files";
import { ProtocolForm } from "@/forms/protocol-form";
import { client } from "@/hono";

export function EditProtocol() {
	/** Params */
	const params = useParams();

	/** Hooks */
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { uploadFn } = useUpload();
	const controller = useFileUpload({
		uploadFn,
		defaultFiles: async () => {
			const response = await client.protocols[":protocolId"].$get({
				param: {
					protocolId: params.protocolId || "",
				},
			});
			const data = await responseHandler(response);
			const protocol = data.protocol as Protocol;
			const files = protocol._files.map((file) => file.file) || [];
			return files;
		},
	});
	const qc = useQueryClient();
	const methods = useForm({
		defaultValues: async () => {
			const response = await client.protocols[":protocolId"].$get({
				param: {
					protocolId: params.protocolId || "",
				},
			});
			const data = await responseHandler(response);
			return data.protocolForm as ProtocolFormSchema;
		},
		resolver: resolver(protocolFormSchema),
		mode: "onChange",
	});
	// The breadcrumb names the protocol as its title is typed, and the way the protocol's own page
	// does while it has none.
	const protocolTitle = useWatch({ control: methods.control, name: "title" });
	useBreadcrumbTitle(
		params.protocolId,
		methods.formState.isLoading ? null : protocolTitle || t("protocols.untitled"),
	);

	async function handleOnSubmit(json: ProtocolFormSchema) {
		try {
			const uploadResults = await controller.handleOnFileUpload();
			await client.protocols[":protocolId"].$patch({
				param: {
					protocolId: params.protocolId || "",
				},
				json: {
					...json,
					uploadResults,
				},
			});
			await qc.invalidateQueries({ predicate: invalidate(["protocols"]) });
			navigate(-1);
		} catch (error) {
			console.error(error);
		}
	}

	async function handleOnReject(errors: typeof methods.formState.errors) {
		toast.error("Failed to create protocol");
		console.info(errors);
	}

	if (methods.formState.isLoading) return <LoadingScreen />;

	/** Render */
	return (
		<FormProvider {...methods}>
			<ContentBox bottomPadding="none">
				<form
					className="w-full h-full"
					onSubmit={methods.handleSubmit(handleOnSubmit, handleOnReject)}
				>
					<ProtocolForm controller={controller} />
				</form>
			</ContentBox>
		</FormProvider>
	);
}
